import { formatDateTime } from './RiskCard';

const KIND_DOT = {
  activation: 'bg-violet-500',
  created: 'bg-sky-500',
  deleted: 'bg-rose-500',
  restored: 'bg-emerald-500',
  recalculation: 'bg-amber-500',
  settings: 'bg-sky-500',
  inputs: 'bg-amber-500',
  integration: 'bg-emerald-500',
  bind: 'bg-sky-500',
  replace: 'bg-rose-500',
  journal: 'bg-slate-500',
};

function formatFullDateTime(value) {
  if (!value) {
    return 'дата неизвестна';
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function TimelineRow({ event, isLast = false, pending = false }) {
  const dot = KIND_DOT[event.kind] || 'bg-slate-400';

  return (
    <li className="relative flex gap-3 pb-3 last:pb-0">
      {!isLast ? (
        <span
          className="absolute top-3 left-[0.55rem] h-[calc(100%-0.35rem)] w-px bg-slate-200 dark:bg-slate-700"
          aria-hidden="true"
        />
      ) : null}
      <span
        className={`relative z-[1] mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white dark:ring-slate-950 ${dot} ${
          pending ? 'opacity-50' : ''
        }`}
        aria-hidden="true"
      />
      <div className={`min-w-0 flex-1 ${pending ? 'opacity-80' : ''}`}>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{event.title}</p>
          {pending ? (
            <span className="rounded-full border border-amber-300/50 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
              В процессе
            </span>
          ) : null}
          {event.at ? (
            <time className="text-[11px] tabular-nums text-slate-500" dateTime={event.at}>
              {formatDateTime(event.at)}
            </time>
          ) : null}
        </div>
        {event.subtitle ? <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{event.subtitle}</p> : null}
        {event.actor ? <p className="mt-0.5 text-[11px] text-slate-400">Кто: {event.actor}</p> : null}
      </div>
    </li>
  );
}

/** Compact activation birthday + vertical lifecycle timeline. */
export default function DeviceTimeline({ timeline, isLoading = false, error = null }) {
  if (isLoading && !timeline) {
    return (
      <div className="mt-4 animate-pulse space-y-3">
        <div className="h-16 rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40" />
        <div className="h-24 rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="mt-4 rounded-xl border border-amber-300/40 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
        {error}
      </p>
    );
  }

  if (!timeline?.activation) {
    return null;
  }

  const events = timeline.events || [];
  const pending = timeline.pending || [];
  const allRows = [...events, ...pending];

  return (
    <div className="mt-4 space-y-3">
      <div className="rounded-xl border border-violet-300/40 bg-violet-50/70 px-3 py-2.5 dark:border-violet-500/30 dark:bg-violet-500/10">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-violet-700/80 dark:text-violet-200/80">
              День рождения
            </p>
            <p className="mt-0.5 text-sm font-semibold text-violet-950 dark:text-violet-50">
              {timeline.activation.title}
            </p>
            {timeline.activation.subtitle ? (
              <p className="mt-0.5 text-xs text-violet-800/80 dark:text-violet-100/70">{timeline.activation.subtitle}</p>
            ) : null}
          </div>
          <time
            className="rounded-lg bg-white/70 px-2 py-1 text-xs font-medium tabular-nums text-violet-900 dark:bg-slate-950/40 dark:text-violet-100"
            dateTime={timeline.activation.at || undefined}
          >
            {formatFullDateTime(timeline.activation.at)}
          </time>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/30">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">События</p>
        {allRows.length === 0 ? (
          <p className="text-xs text-slate-500">Пока нет других событий</p>
        ) : (
          <ul>
            {allRows.map((event, index) => (
              <TimelineRow
                key={event.id}
                event={event}
                isLast={index === allRows.length - 1}
                pending={event.status === 'pending'}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
